import axios from 'axios';
import { teamsService } from '../teams/teamsService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface TeamsAuthCredentials {
    teamsToken?: string;
    teamsContext: any;
}

export interface User {
    id: number | string;
    email: string;
    role: 'doctor' | 'support' | 'admin';
    firstName: string;
    lastName: string;
    displayName?: string;
    department?: string;
    jobTitle?: string;
    specialty?: string;
    licenseNumber?: string;
    isTeamsUser?: boolean;
    authProvider?: 'standard' | 'teams-sso';
}

export interface LoginResponse {
    success: boolean;
    message: string;
    token: string;
    user: User;
}

class AuthService {
    private token: string | null = null;
    private user: User | null = null;

    constructor() {
        // Check for existing token in localStorage on initialization
        const savedToken = localStorage.getItem('authToken');
        if (savedToken) {
            this.token = savedToken;
            // Set authorization header if token exists
            axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        }

        const savedUser = localStorage.getItem('authUser');
        if (savedUser) {
            try {
                this.user = JSON.parse(savedUser);
            } catch (error) {
                console.error('Error parsing saved user:', error);
                this.clearAuth();
            }
        }
    }

    async login(credentials: LoginCredentials): Promise<LoginResponse> {
        try {
            const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials);

            if (response.data.success) {
                const { token, user } = response.data;

                // Type guard: ensure token exists before using it
                if (!token) {
                    throw new Error('No token received from server');
                }

                this.token = token;
                this.user = user;

                // Save to localStorage (TypeScript now knows token is string)
                localStorage.setItem('authToken', token);
                localStorage.setItem('authUser', JSON.stringify(user));

                // Set default authorization header for future requests
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            }

            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Login failed');
        }
    }

    async loginWithTeams(credentials: TeamsAuthCredentials): Promise<LoginResponse> {
        try {
            console.log('🔐 Attempting Teams SSO authentication...');

            const response = await axios.post(`${API_BASE_URL}/auth/teams-sso`, {
                teamsToken: credentials.teamsToken,
                teamsContext: credentials.teamsContext,
            });

            if (response.data.success) {
                const { token, user, teamsContext } = response.data;

                if (!token) {
                    throw new Error('No token received from Teams SSO');
                }

                this.token = token;
                this.user = user;

                // Save to localStorage
                localStorage.setItem('authToken', token);
                localStorage.setItem('authUser', JSON.stringify(user));
                localStorage.setItem('teamsContext', JSON.stringify(teamsContext));

                // Set default authorization header
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                console.log(`✅ Teams SSO successful: ${user.displayName} (${user.role})`);
            }

            return response.data;
        } catch (error: any) {
            console.error('❌ Teams SSO authentication failed:', error);
            throw new Error(error.response?.data?.message || 'Teams SSO authentication failed');
        }
    }

    async authenticateWithTeamsContext(): Promise<LoginResponse | null> {
        try {
            console.log('🔄 Checking for existing Teams context...');

            // Get Teams context
            const teamsContext = await teamsService.initialize();

            if (!teamsContext.isInTeams) {
                console.log('⚠️ Not running in Teams - falling back to standard auth');
                return null;
            }

            console.log('📱 Running in Teams - attempting SSO authentication');

            // Try Teams SSO authentication
            const authResult = await this.loginWithTeams({
                teamsContext: teamsContext,
            });

            return authResult;
        } catch (error: any) {
            console.error('❌ Teams context authentication failed:', error);

            // Check if we should fall back to standard login
            if (error.message.includes('Invalid Teams context')) {
                return null; // Fall back to standard login
            }

            throw error;
        }
    }

    async logout(): Promise<void> {
        try {
            await axios.post(`${API_BASE_URL}/auth/logout`);
        } catch (error) {
            console.error('Logout API call failed:', error);
        } finally {
            this.clearAuth();
        }
    }

    async getTeamsConfiguration(): Promise<any> {
        try {
            const response = await axios.get(`${API_BASE_URL}/auth/teams-config`);
            return response.data;
        } catch (error) {
            console.error('Failed to get Teams configuration:', error);
            return {
                success: false,
                configured: false,
                developmentMode: true,
            };
        }
    }

    async validateTeamsContext(teamsContext: any): Promise<any> {
        try {
            const response = await axios.post(`${API_BASE_URL}/auth/teams-context`, {
                teamsContext,
            });
            return response.data;
        } catch (error) {
            console.error('Teams context validation failed:', error);
            return {
                success: false,
                valid: false,
                reason: 'Validation request failed',
            };
        }
    }

    async refreshToken(): Promise<boolean> {
        if (!this.token) return false;

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
                token: this.token,
            });

            if (response.data.success) {
                const { token, user } = response.data;

                this.token = token;
                this.user = user;

                localStorage.setItem('authToken', token);
                localStorage.setItem('authUser', JSON.stringify(user));
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                return true;
            }

            return false;
        } catch (error) {
            console.error('Token refresh failed:', error);
            this.clearAuth();
            return false;
        }
    }

    async verifyToken(): Promise<boolean> {
        if (!this.token) return false;

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/verify`, {
                token: this.token,
            });

            if (response.data.success) {
                this.user = response.data.user;
                localStorage.setItem('authUser', JSON.stringify(this.user));
                return true;
            } else {
                this.clearAuth();
                return false;
            }
        } catch (error) {
            console.error('Token verification failed:', error);
            this.clearAuth();
            return false;
        }
    }

    getCurrentUser(): User | null {
        return this.user;
    }

    getToken(): string | null {
        return this.token;
    }

    isAuthenticated(): boolean {
        return this.token !== null && this.user !== null;
    }

    hasRole(role: string): boolean {
        return this.user?.role === role;
    }

    private clearAuth(): void {
        this.token = null;
        this.user = null;
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        localStorage.removeItem('teamsContext');
        delete axios.defaults.headers.common['Authorization'];
    }

    // =================== TEAMS-SPECIFIC HELPER METHODS ===================

    isTeamsUser(): boolean {
        return this.user?.isTeamsUser === true;
    }

    getTeamsContext(): any | null {
        const savedContext = localStorage.getItem('teamsContext');
        if (savedContext) {
            try {
                return JSON.parse(savedContext);
            } catch (error) {
                console.error('Error parsing Teams context:', error);
                return null;
            }
        }
        return null;
    }

    getAuthProvider(): string {
        return this.user?.authProvider || 'standard';
    }

    async checkTeamsAvailability(): Promise<boolean> {
        try {
            const config = await this.getTeamsConfiguration();
            return config.configured && !config.developmentMode;
        } catch (error) {
            return false;
        }
    }

    // Enhanced authentication check that considers Teams context
    async isAuthenticatedWithContext(): Promise<boolean> {
        if (!this.isAuthenticated()) {
            return false;
        }

        // If user is a Teams user, verify Teams context is still valid
        if (this.isTeamsUser()) {
            const teamsContext = this.getTeamsContext();
            if (teamsContext) {
                const validation = await this.validateTeamsContext(teamsContext);
                return validation.valid;
            }
        }

        return true;
    }
}

export const authService = new AuthService();
